// Shared state for authored transition sequences.
//
// The transition host is mounted in `app/root.tsx`, OUTSIDE the router, so it
// survives the route swap it is hiding. That's the whole architectural trick: a
// sequence choreographed inside either page would be torn down mid-flight by
// the very navigation it's covering. The host owns the timing; this store owns
// only what other components need to see — which sequence is running, and where
// its shared elements start and land.
//
// Geometry is a NAMED SLOT MAP rather than a source/target pair. The tape
// transport needs two slots (Welcome's title → Menu's wordmark); other
// sequences need different ones, or none. Named slots mean a new sequence can
// declare its own without touching this file.

import { create } from 'zustand';

/** Which authored sequence is playing. Resolved against the renderer registry. */
export type TransitionKind = 'tape' | 'splice' | 'advance';

/**
 * Plain geometry rather than DOMRect. We only ever need these four numbers, and
 * a DOMRect read from a since-unmounted element is a footgun.
 */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type TapePhase =
  /** Nothing running. */
  | 'idle'
  /** Sequence opening; the origin route is still visible beneath. */
  | 'spinup'
  /** Cover at full opacity. The route swap happens in here, unseen. */
  | 'covered'
  /** Cover retracting; the destination is threading out. */
  | 'settling';

interface TransitionState {
  kind: TransitionKind | null;
  phase: TapePhase;
  /**
   * Increments per run. The destination route compares this against the value
   * it saw at mount to know whether IT was the page the sequence delivered.
   */
  runId: number;
  /** Named geometry for shared elements, e.g. `{ title: rect, wordmark: rect }`. */
  slots: Record<string, Rect>;
  /**
   * Non-geometric context the running sequence needs to render — the splice
   * shows the two crew names on its halves, for instance. Kept here so a
   * renderer never has to reach into the match store and couple itself to game
   * state it has no business knowing about.
   */
  payload: Record<string, string>;
  /** Fired by the host at `commitAt`, hidden behind the cover. */
  commit: (() => void) | null;

  start: (opts: {
    kind: TransitionKind;
    commit: () => void;
    slots?: Record<string, Rect>;
    payload?: Record<string, string>;
  }) => boolean;
  setPhase: (phase: TapePhase) => void;
  setSlot: (name: string, rect: Rect) => void;
  end: () => void;
}

export const useTransitionStore = create<TransitionState>((set, get) => ({
  kind: null,
  phase: 'idle',
  runId: 0,
  slots: {},
  payload: {},
  commit: null,

  start: ({ kind, commit, slots, payload }) => {
    // Guard double-taps. A second press mid-sequence must not restart the
    // sequence or fire navigate() twice.
    if (get().phase !== 'idle') return false;
    set((s) => ({
      kind,
      phase: 'spinup',
      runId: s.runId + 1,
      slots: slots ?? {},
      payload: payload ?? {},
      commit,
    }));
    return true;
  },

  setPhase: (phase) => set({ phase }),

  setSlot: (name, rect) => set((s) => ({ slots: { ...s.slots, [name]: rect } })),

  end: () =>
    set({ kind: null, phase: 'idle', slots: {}, payload: {}, commit: null }),
}));

/** Measure an element into the plain-geometry shape the store holds. */
export function measureRect(el: Element | null): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.x, y: r.y, width: r.width, height: r.height };
}

/**
 * Ref callback factory for a shared element's landing slot.
 *
 *     <span ref={registerSlot('wordmark')}>
 *
 * Only records while a sequence is actually running — outside a run the element
 * is just an element, and stale geometry lying around is a liability. Never
 * clobbers a good rect with null, because a destination may register several
 * responsive variants of the same mark and the ones hidden at the current
 * breakpoint measure 0×0.
 */
const slotCallbacks = new Map<string, (el: Element | null) => void>();

export function registerSlot(name: string) {
  // Cached by name so the ref identity is stable across renders. Returning a
  // fresh closure each render would make React detach and reattach the ref —
  // and therefore re-measure — on every single render of the host component.
  const existing = slotCallbacks.get(name);
  if (existing) return existing;

  const fn = (el: Element | null) => {
    const { phase, setSlot } = useTransitionStore.getState();
    if (phase === 'idle' || !el) return;
    const rect = measureRect(el);
    if (rect) setSlot(name, rect);
  };
  slotCallbacks.set(name, fn);
  return fn;
}

/** True while any part of a sequence is on screen. */
export function useTransitionActive(): boolean {
  return useTransitionStore((s) => s.phase !== 'idle');
}
